import head from 'lodash/head'
import isEmpty from 'lodash/isEmpty'
import { CERTIFIED, PREMIUM, RAYBAN_STORES } from '@/scripts/constants/store-locator'
import { LC_ICO, SB } from '@/scripts/constants/physical-stores'
import { MEMBER_ID, ORDER_ID } from '@/scripts/constants/parameters'
import { MOBILE, TABLET } from '@/scripts/constants/media-query'

import axios, { AxiosResponse } from 'axios'
import makeDebug from 'debug'
import qs from 'qs'
import wcs from '@/scripts/libs/wcs'
import { PhysicalStoreType } from '../types/enums'
import {
  ajaxShipChargeUpdatePostParams,
  getStoreListFunction,
  getStoreListParameters,
  PhysicalStore,
  ShipFromStoreOption,
} from '@/scripts/libs/physical-stores/types/interfaces'
import {
  WcsEUStore,
  WcsEUStoreResponse,
  WcsFindInStoreShippingSaveControllerPickupStore,
  WcsPhysicalStore,
  WcsStoreListCallParamsForEU,
  WcsSearchStoreListCallParamsForNorthAmerica,
  WcsUPSCallParams,
  WcsUPSCallResponse,
  WcsUPSStore,
} from '@/scripts/libs/physical-stores/types/wcs-interfaces'
import { PhysicalStores } from '../physical-stores'
import {
  EUCountriesWithPhysicalStoresService,
  NACountriesWithPhysicalStoresService,
} from '@/scripts/constants/physical-stores'
import { WcsLocales } from '@/scripts/libs/wcs/types/locales'

const debug = makeDebug('cart:clickAndCollect')
const { catalogId, isPickupEnabled, langId, locale, storeId } = wcs.getStoreConfig()
const errorResponseObjectForStoreListCalls = { success: false }

export class WcsPhysicalStores {
  private static mapChosenStore = function (
    store: PhysicalStore,
    orderId: number
  ): WcsFindInStoreShippingSaveControllerPickupStore {
    const bestCallingTime = store?.bestCallingTime
    const organizationName = store?.shipToName1 ?? ''
    const address1 = store?.shipToStras ?? ''
    const address2 = store?.shipToOrt1 ?? ''
    const shipModeId = store?.shippingId ? +store.shippingId : 0
    const memberIdField = document.getElementById(MEMBER_ID) as HTMLInputElement

    return {
      storeId,
      catalogId,
      langId,
      addressType: 'PU',
      memberId: memberIdField?.value ?? null,
      orderId,
      organizationName,
      bestCallingTime,
      field1: store.latitude,
      field2: store.longitude,
      field3: 'lc-ico',
      sapStoreNumber: +store.externalIdentifier,
      phoneNumberUPS: store.telephone,
      address1,
      address2,
      address3: '',
      zipCode: store.postalCode,
      city: store.city,
      state: store.state ?? '',
      country: store.country,
      shipModeId,
      onlyShippingAddr: true,
      URL: 'PaymentDetailsView',
    }
  }

  static chooseStoreForSFS = async (
    {
      address1,
      address2,
      zipCode,
      city,
      state,
      country,
    }: {
      address1: string
      address2: string
      zipCode: string
      city: string
      state: string
      country: string
    },
    shipModeId: number,
    orderId: number
  ) => {
    const endPoint = wcs.getEndPoint('shippingSaveControllerPickUpCmd')

    const postParams = {
      address1,
      address2,
      addressType: SB,
      catalogId,
      city,
      country,
      field3: LC_ICO,
      langId,
      memberId: document.getElementById(MEMBER_ID) || '',
      onlyShippingAddr: true,
      orderId,
      shipModeId,
      state,
      storeId,
      URL: 'PaymentDetailsView',
      zipCode: zipCode.split('-')[0],
    }

    try {
      debug('Store was chosen with next post params %o', postParams)
      const result = await axios.post(endPoint, qs.stringify(postParams))
      const payload = result.data
      debug(payload)
      return payload
    } catch (e) {
      if (e instanceof Error) {
        debug(e.message)
        return {
          error: true,
        }
      }
    }
  }

  static chooseStoreAndPerformShippingModeUpdate = async (
    chosenStore: PhysicalStore,
    orderId: number,
    $mq: any,
    isInCart: boolean,
    isFromStoreDetails: boolean
  ) => {
    if (chosenStore?.isBops || isInCart) {
      await WcsPhysicalStores.chooseStore(chosenStore, orderId)
      const result = await WcsPhysicalStores.performShippingModeUpdate(chosenStore, orderId)

      if ($mq === MOBILE || $mq === TABLET) {
        const storeEl = document.getElementById('store') as HTMLInputElement
        const shipModeId = chosenStore?.shippingId
        if (storeEl?.value && shipModeId) {
          const shipModeIdKey = `rayban.${storeEl?.value}.shipModeId`
          sessionStorage.setItem(shipModeIdKey, shipModeId)
          if (isInCart && window.cart?.getAvailability) {
            window.cart.getAvailability()
          }

          if (!result || (result as { error: boolean }).error === true) {
            return
          }

          if (wcs.isInCart() && !!window.performOrderDetailsJsonResponse && isFromStoreDetails) {
            window.performOrderDetailsJsonResponse()
          }
        }
      } else {
        if (window.displayDeliveryMessage) {
          window.displayDeliveryMessage()
        }

        if (!result) {
          return
        }

        if ((result as { error: boolean }).error === true) {
          return
        }

        if (
          wcs.isInCart() &&
          !!window.performAjaxOrderItemUpdateAfterChooseStore &&
          isFromStoreDetails
        ) {
          window.performAjaxOrderItemUpdateAfterChooseStore(result as AxiosResponse<any>)
        }
      }
    }
  }
  private static chooseStore = async function (chosenStore: PhysicalStore, orderId: number) {
    const postParams = WcsPhysicalStores.mapChosenStore(chosenStore, orderId)
    const endPoint = wcs.getEndPoint('shippingSaveControllerPickUpCmd')
    postParams.isPickupStore = true
    try {
      debug('Store was chosen with next post params %o', postParams)
      const result = await axios.post(endPoint, qs.stringify(postParams))
      const payload = result.data
      debug(payload)
    } catch (e) {
      if (e instanceof Error) {
        debug(e.message)
        return {
          error: true,
        }
      }
    }
  }

  static performShipFromStoreUpdate = async ({
    orderId,
    shipModeId,
    sapStoreNumber,
    deliveryOption,
  }: {
    orderId: number
    shipModeId: number
    sapStoreNumber: number
    deliveryOption: ShipFromStoreOption
  }) => {
    const endpoint = wcs.getEndPoint('ajaxShipChargeUpdate')

    const postParams = {
      orderId,
      shipModeId,
      sapStoreNumber,
      scheduledDeliveryDate: deliveryOption?.scheduledDeliveryDate,
      sfsDeliveryOption: deliveryOption?.name,
      deliverBetweenStart: deliveryOption?.hours?.start,
      deliverBetweenEnd: deliveryOption?.hours?.end,
      pickupAfter: deliveryOption?.hours?.pickUpAfter,
    }

    const response = await axios.post(endpoint, qs.stringify(postParams))
    if (wcs.isInCart()) {
      window.checkColoradoTax(response.data.orderDetails?.ColoradoTax)
    }

    return response
  }

  private static performShippingModeUpdate = async function (
    store: PhysicalStore,
    orderId: number
  ) {
    if (!store?.shippingId) {
      return
    }
    const postParams: ajaxShipChargeUpdatePostParams = {
      orderId,
      shipModeId: store?.shippingId,
      sapStoreNumber: store.externalIdentifier,
    }

    if (wcs.isInCart() && EUCountriesWithPhysicalStoresService.includes(storeId)) {
      postParams.isPickUpPoint = true
    }

    const endPoint = wcs.getEndPoint('ajaxShipChargeUpdate')
    try {
      debug('Store was updated with next post params %o', postParams)
      const result = await axios.post(endPoint, qs.stringify(postParams))

      const payload = result.data
      window.checkColoradoTax(payload.orderDetails?.ColoradoTax)
      debug(payload)
      return result
    } catch (e) {
      if (e instanceof Error) {
        debug(e.message)
        return {
          error: true,
        }
      }
    }
  }

  static fetchShipFromStorePhysicalStores = async ({
    latitude,
    longitude,
    orderId,
  }: {
    [key: string]: number
  }) => {
    const availabilityEndpoint = wcs.getEndPoint('shipFromStoreUrl')
    const response = await axios.get(availabilityEndpoint, {
      params: {
        latitude,
        longitude,
        orderId,
      },
    })

    const responseObjectKeys = Object.keys(response.data)

    if (responseObjectKeys.length === 1 && responseObjectKeys.includes('Message')) {
      return {
        isAvailable: false,
        message: response.data.Message,
      }
    }

    if (response.data?.PhysicalsStores?.length === 0) {
      return null
    } else {
      const stores = response.data?.PhysicalsStores.map(
        WcsPhysicalStores.normalizePhysicalStoreBySearchType(PhysicalStoreType.SHIP_FROM_STORE)
      )
      const firstStore = head(stores) as any
      if (firstStore?.sfsOptions?.length > 1) {
        return firstStore
      } else {
        const nextStore = stores.find(
          (store: PhysicalStore) => store?.sfsOptions && store?.sfsOptions?.length > 1
        )
        return nextStore ?? null
      }
    }
  }

  // TODO: extract activeFilters with some enum
  static fetchStoreLocatorPhysicalStores = async (
    endpoint: string,
    latitude: number,
    longitude: number,
    prescriptionglasses: boolean,
    activeFilters: string[],
    countryCode = '',
    baseUrl = ''
  ) => {
    const params: any = {}
    params.radius = 20
    if (latitude) {
      params.latitude = latitude
    }
    if (longitude) {
      params.longitude = longitude
    }
    activeFilters.forEach((activeFilter) => {
      params[activeFilter] = true
    })

    if (prescriptionglasses) {
      params.prescriptionglasses = true
      params.countryCode = countryCode
    }

    const storesToFilterQueryParam = WcsPhysicalStores.getStoresToFilterQueryParam()
    if (storesToFilterQueryParam) {
      params.storesToFilter = storesToFilterQueryParam
    }

    const stringifiedParams = qs.stringify(params)
    const url = `${endpoint}?${stringifiedParams}`

    try {
      const response = await axios.get(url)
      const stores = response.data?.map(
        WcsPhysicalStores.normalizePhysicalStoreBySearchType(
          PhysicalStoreType.STORE_LOCATOR,
          baseUrl
        )
      )
      return stores
    } catch (e) {
      debug(e)
      return []
    }
  }

  private static getStoresToFilterQueryParam() {
    const queryParams = new URLSearchParams(window.location.search)
    const searchTermQueryParam = queryParams.get('storesToFilter') ?? ''
    return searchTermQueryParam
  }

  private static sortStoresByDistance = (storeA: PhysicalStore, storeB: PhysicalStore) => {
    return storeA.distance - storeB.distance
  }

  static mapAndSortPhysicalStores = (stores: PhysicalStore[]) => {
    const rayBanStores = stores
      .filter((store) => store.storeType === RAYBAN_STORES)
      .sort(WcsPhysicalStores.sortStoresByDistance)
    const premiumResellers = stores
      .filter((store) => store.storeType === PREMIUM)
      .sort(WcsPhysicalStores.sortStoresByDistance)
    const resellers = stores
      .filter((store) => store.storeType === CERTIFIED)
      .sort(WcsPhysicalStores.sortStoresByDistance)

    return [...rayBanStores, ...premiumResellers, ...resellers]
  }

  static sortCityIndiaStoreLocator = (stores: PhysicalStore[]) => {
    const premiumResellers = stores.filter((store) => store.storeType === PREMIUM)
    const resellers = stores.filter((store) => store.storeType === CERTIFIED)
    const storesFiltered = [...premiumResellers, ...resellers]
    return storesFiltered.sort((previousStore, nextStore) => {
      if (previousStore.city < nextStore.city) {
        return -1
      }
      if (previousStore.city > nextStore.city) {
        return 1
      }
      return 0
    })
  }

  static getStoresList: getStoreListFunction = async function (parameters: getStoreListParameters) {
    const { city, isInCart, latitude, longitude, partNumber, radius } = parameters
    const isCountryInEU = EUCountriesWithPhysicalStoresService.includes(storeId)
    const isCountryInNA = NACountriesWithPhysicalStoresService.includes(storeId)
    const fixedRadius = WcsPhysicalStores.getFixedRadius(radius)

    if (isCountryInNA) {
      const params = WcsPhysicalStores.getParamsForPhysicalStoreInNorthAmerica(
        latitude,
        longitude,
        fixedRadius,
        partNumber,
        isInCart
      )
      const stringifiedParams = qs.stringify(params)
      const searchStoreListEndpoint = wcs.getEndPoint('searchStoreList')
      const url = `${searchStoreListEndpoint}?${stringifiedParams}`

      const response = await WcsPhysicalStores.getResponse(url)

      if (isEmpty(response)) return errorResponseObjectForStoreListCalls

      const storeList = WcsPhysicalStores.getStoreListForNA(response.PhysicalsStores)
      return {
        storeList,
        storeListState: true,
        success: true,
      }
    }

    if (isCountryInEU) {
      const storeListCallresponse = await WcsPhysicalStores.makeStoreListCallInEU(
        latitude,
        longitude,
        100,
        fixedRadius,
        false
      )

      let UPSCallResponse: WcsUPSCallResponse = {}

      if (isPickupEnabled) {
        UPSCallResponse = await WcsPhysicalStores.makeUPSCall(city)
      }

      if (isEmpty(UPSCallResponse) && isEmpty(storeListCallresponse)) {
        return errorResponseObjectForStoreListCalls
      }

      const storeList = await WcsPhysicalStores.getStoreListForEU(
        storeListCallresponse,
        UPSCallResponse
      )
      return {
        storeList,
        storeListState: true,
        success: true,
      }
    }

    return errorResponseObjectForStoreListCalls
  }

  private static normalizeEUPhysicalStore = () => {
    return (store: WcsEUStore) => PhysicalStores.normalizeEUPhysicalStore(store)
  }

  private static normalizeUPSPoint = (shippingId: string) => {
    return (store: WcsUPSStore) => PhysicalStores.normalizeUPSPhysicalStore(store, shippingId)
  }

  private static normalizePhysicalStoreBySearchType = (type: PhysicalStoreType, baseUrl = '') => {
    return (store: WcsPhysicalStore) => PhysicalStores.normalizePhysicalStore(store, type, baseUrl)
  }

  private static getParamsForUPSCall = (city: string) => {
    const countryCode = WcsLocales.mapCountryCode(locale)
    const params: WcsUPSCallParams = {
      catalogId: +catalogId,
      city,
      countryCode,
      langId: +langId,
      storeId,
    }
    return params
  }

  private static getParamsForStoreListCallInEU = (
    maxItems: number,
    radius: number,
    siteLevelStoreSearch: boolean
  ) => {
    const params: WcsStoreListCallParamsForEU = {
      maxItems,
      radius,
      siteLevelStoreSearch,
    }

    const orderIdEl = document.getElementById(ORDER_ID) as HTMLInputElement
    const orderId = orderIdEl?.value
    if (wcs.isInCart() && orderId) {
      params.orderId = orderId
    }

    return params
  }

  private static getParamsForPhysicalStoreInNorthAmerica = (
    latitude: number,
    longitude: number,
    radius: number,
    partNumber: number,
    isInCart: boolean
  ) => {
    const orderIdEl = document.getElementById(ORDER_ID) as HTMLInputElement

    const params: WcsSearchStoreListCallParamsForNorthAmerica = {
      latitude,
      longitude,
      radius,
    }

    if (orderIdEl && orderIdEl.value && isInCart) {
      params.orderId = orderIdEl.value
    }

    if (partNumber && !isInCart) {
      params.partNumber = partNumber
    }

    return params
  }

  private static getStoreListForEU = async (
    storeListCallresponse: WcsEUStoreResponse,
    UPSCallResponse: WcsUPSCallResponse
  ) => {
    const stores =
      storeListCallresponse?.PhysicalStore?.map(WcsPhysicalStores.normalizeEUPhysicalStore()) ?? []

    const shippingIdForUPSCALL = UPSCallResponse.LocatorResponse?.SearchResults?.shippingId ?? ''
    const UPSStores =
      UPSCallResponse?.LocatorResponse?.SearchResults?.DropLocation?.map(
        WcsPhysicalStores.normalizeUPSPoint(shippingIdForUPSCALL)
      ) ?? []

    const storeList = [...stores, ...UPSStores]
    return storeList
  }

  private static getStoreListForNA(response: WcsPhysicalStore[]) {
    const storeList = response.map(
      WcsPhysicalStores.normalizePhysicalStoreBySearchType(PhysicalStoreType.CLICK_AND_COLLECT)
    )
    return storeList
  }

  private static makeUPSCall = async (city: string) => {
    const UPSparams = WcsPhysicalStores.getParamsForUPSCall(city)
    const stringifiedParams = qs.stringify(UPSparams)
    const UPSCallEndpoint = 'webapp/wcs/stores/servlet/UPSFetchPickupPointAjaxCmd'
    const url = `${UPSCallEndpoint}?${stringifiedParams}`
    const UPSCallResponse: WcsUPSCallResponse = await WcsPhysicalStores.getResponse(url)

    return UPSCallResponse
  }

  private static makeStoreListCallInEU = async (
    latitude: number,
    longitude: number,
    maxItems: number,
    radius: number,
    siteLevelStoreSearch: boolean
  ) => {
    const params = WcsPhysicalStores.getParamsForStoreListCallInEU(
      maxItems,
      radius,
      siteLevelStoreSearch
    )
    const stringifiedParams = qs.stringify(params)
    const storeListForEUEndpoint = `wcs/resources/store/${storeId}/storelocator/latitude/${latitude}/longitude/${longitude}`
    const url = `${storeListForEUEndpoint}?${stringifiedParams}`

    const response: WcsEUStoreResponse = await WcsPhysicalStores.getResponse(url)

    return response
  }

  private static getResponse = async (url: string) => {
    try {
      const response = await axios.get(url)
      const responseData = response.data
      return responseData
    } catch (e) {
      debug(e)
      return {}
    }
  }

  private static getFixedRadius = (radius: number) => {
    return +radius < 1 ? 1 : +radius
  }

  static fetchRayBanStoresWithBookAnAppointment = async () => {
    try {
      const endpoint = wcs.getEndPoint('bookAnAppointment')
      const response = await axios.get(endpoint)
      return response.data
    } catch (e) {
      debug(e)
      return []
    }
  }

  static fetchSGHStoresWithBookAnAppointment = async () => {
    try {
      const endpoint = wcs.getEndPoint('bookAnAppointment')
      const sghEndpoint = endpoint.replace('RB', 'SGH')
      const response = await axios.get(sghEndpoint)
      return response.data
    } catch (e) {
      debug(e)
      return []
    }
  }
}
